import { IconButton } from '@material-ui/core';
import CloseIcon from '@mui/icons-material/Close';
import { createTheme, ThemeProvider } from '@mui/material';
import { styled } from '@mui/material/styles';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useWindowWidth } from '@react-hook/window-size';
import moment from 'moment';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { InView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import SwipeableViews from 'react-swipeable-views';
import cn from 'classnames';
import * as PropTypes from 'prop-types';
import React, { createRef, useEffect, useState } from 'react';
import Carousel from 'react-multi-carousel';
import { httpGet, httpPost } from '../../utils/httpClient';
import stickyRef from '../../utils/lazyRef';
import {
  BCBreadcrumbs,
  CircleRating,
  ProgressMeter,
  VintageButton,
} from '../index';
import Map from '../Map/Map';
import styles from './HotelReview.module.scss';
import 'react-multi-carousel/lib/styles.css';
import { replaceHTML } from '../utils/replaceHTML';
import { CustomLeftArrow, CustomRightArrow } from '../Carousel/arrows/arrows';
import qs from 'qs';
import PlaceIcon from '@mui/icons-material/Place';
import MobileFiltersHeader from '../HotelFilters/HotelsFilter/MobileFiltersHeader';

const SocialMediaLinks = dynamic(() =>
  import('../SocialMediaLinks/SocialMediaLinks')
);
const CallToSignUp = dynamic(() => import('../CallToSignUp/CallToSignUp'));
const HotelSearch = dynamic(() => import('../HotelSearch/HotelSearch'));
const RecommendationsList = dynamic(() =>
  import('../RecommendationsList/RecommendationsList')
);
const HotelOffers = dynamic(() => import('../HotelOffers/HotelOffers'));
const StyledTabs = styled((props) => (
  <Tabs
    {...props}
    variant="scrollable"
    scrollButtons="auto"
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
  />
))({
  '& .MuiBox-root': {
    borderTop: '1px solid #86694B',
    borderBottom: '1px solid #86694B',
  },
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  '& .MuiTabs-indicatorSpan': {
    width: '100%',
    backgroundColor: 'transparent',
  },
});

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: '1.25rem',
    fontFamily: 'PPFragment, serif',
    color: '#86694B',
    '&.Mui-selected': {
      color: '#202020',
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'transparent',
    },
    '&.MuiButtonBase-root': {
      padding: 16,
      height: 64,
      maxHeight: 64,
      minHeight: 64,
      fontSize: '1.25rem',
      fontFamily: 'PPFragment, serif',
    },
  })
);
const tabsTheme = createTheme({
  palette: {
    primary: {
      main: '#202020',
    },
    secondary: {
      main: '#202020',
    },
  },
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const carouselRef = createRef();
const responsive = {
  desktop: {
    breakpoint: { max: 99999, min: 1024 },
    items: 3,
    slidesToSlide: 3,
  },

  mobile: {
    breakpoint: { max: 1023, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
};
const getHotelReviewHCMetadata = async (hotel, currency) => {
  const general = await httpGet(
    `/api/hotel/booking/${hotel}?currency=${currency}`
  );
  return { general: await general.json() };
};
const getHotelReviewHCOffers = async (hotel, query) => {
  const general = await (
    await httpPost(`/api/hotel/booking/${hotel}`, query)
  ).json();
  return { general };
};
export const HCHotelReview = ({
  review,
  recommendation,
  hotelId,
  authCompleted,
}) => {
  const router = useRouter();
  const userStore = useSelector((state) => state.user.value);
  const [query, setQuery] = useState(null);
  const [searchValue, setSearchValue] = useState(null);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [offersError, setOffersError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [searchToggled, toggleSearch] = useState(true);
  const { elementRef, isSticky } = stickyRef();
  useEffect(() => {
    toggleSearch(true);
  }, [isSticky]);

  const mapQuery = (q) => {
    return {
      destination: q.autocomplete?.k || null,
      currency: userStore.currency,
      checkin: q.dateRange?.departure,
      checkout: q.dateRange?.return,
      rooms: q.visitors?.map((room) => {
        return {
          adult: +room.adults,
          ...(room.childrenAges?.length && {
            children: room.childrenAges.map((c) => +c),
          }),
        };
      }),
    };
  };
  const width = useWindowWidth();
  const [tab, setTab] = useState(1);
  const [nights, setNights] = useState(0);
  const [hcMetadata, setHcMetadata] = useState(null);
  const [hcOffers, setHcOffers] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const [activityExpanded, setActivityExpanded] = useState(false);
  const [generalExpanded, setGeneralExpanded] = useState(false);
  const [servicesExpanded, setServicesExpanded] = useState(false);
  const [stickySearchTop, setStickySearchTop] = useState(73);
  const getActivity = () =>
    hcMetadata?.general.HC?.featureSummary
      .find((amenity) => amenity.name === 'Activities')
      ?.description.split(',') || '';
  const getGeneral = () =>
    hcMetadata?.general.HC?.featureSummary
      .find((amenity) => amenity.name === 'Services')
      ?.description.split(',') || '';
  const getServices = () =>
    hcMetadata?.general.HC?.featureSummary
      .find((amenity) => amenity.name === 'General')
      ?.description.split(',') || '';
  const executeScroll = (ref) => {
    const target = { ...ref };
    target?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const tabsRef = createRef();
  useEffect(() => {
    setIsMobile(width <= 1024);
  }, [width]);
  useEffect(() => {
    if (query) {
      setNights(moment(query.checkout).diff(moment(query.checkin), 'days'));
      if (query?.checkout && query?.checkin) {
        executeScroll(tabsRef);
        setTab(0);
      }
    } else {
      setNights(0);
    }
  }, [query]);
  useEffect(() => {
    if (userStore) {
      if (Object.keys(router.query).length) {
        setSearchValue(qs.parse(router.query));
        setQuery(mapQuery(qs.parse(router.query)));
      }
      const fetchMetadata = async () => {
        setHcMetadata(
          await getHotelReviewHCMetadata(hotelId, userStore.currency)
        );
      };
      fetchMetadata();
    }
  }, [userStore, router]);

  useEffect(() => {
    if (hcMetadata && query?.checkin && query?.checkout && !loadingOffers) {
      setLoadingOffers(true);
      const fetchOffers = async () => {
        setAnalyticsData({
          hotelName: hcMetadata.general.HC.name,
          lengthOfStay: nights, // or '5 days'
          searchId: hcMetadata.general.HC.id,
          destinationCountry: hcMetadata.general.HC.countryCode,
          destinationCity: hcMetadata.general.HC.place.fullName,
          numGuests: query.rooms.reduce((acc = 0, v) => {
            acc +=
              v.childrenAges?.length > 0
                ? +v.adults + v.childrenAges.length
                : +v.adults;
            return acc;
          }, 0),
          numAdults: query.rooms.reduce((acc = 0, v) => {
            acc += +v.adult;
            return acc;
          }, 0),
          numChildren: query.rooms.reduce((acc = 0, v) => {
            acc += v.children?.length;
            return acc;
          }, 0),
        });
        setHcOffers(
          await getHotelReviewHCOffers(hotelId, {
            checkin: query.checkin,
            checkout: query.checkout,
            currency: userStore.currency,
            country: userStore.country,
            rooms: query.rooms,
          })
        );
      };
      fetchOffers();
    }
  }, [hcMetadata]);
  useEffect(() => {
    if (query && query.checkout && query.checkin) {
      if (hcOffers?.general?.HC && !hcOffers.general.HC.isComplete) {
        setTimeout(() => {
          const fetchOffers = async () => {
            setHcOffers(
              await getHotelReviewHCOffers(hotelId, {
                checkin: query.checkin,
                checkout: query.checkout,
                currency: userStore.currency,
                rooms: query.rooms,
              })
            );
          };
          fetchOffers();
        }, 1000);
      }
      if (hcOffers?.general?.HC?.isComplete) {
        setLoadingOffers(false);
      }
      if (hcOffers?.general?.error) {
        setOffersError(hcOffers.general.error);
        setLoadingOffers(false);
      }
      setTab(0);
    }
  }, [hcOffers]);

  const handleChange = (event, newValue) => {
    setTab(newValue);
  };

  const renderSearch = (toggler, viewMode, preventCollapsing = false) => {
    return (
      <InView triggerOnce>
        {({ inView, ref }) => (
          <div className="mx-auto" ref={ref}>
            {inView && (
              <HotelSearch
                value={searchValue}
                viewMode={viewMode}
                hotelName={hcMetadata?.general?.HC?.name}
                hotelId={hotelId}
                toggleSearch={toggler}
                collapsed={!preventCollapsing && isSticky}
              />
            )}
          </div>
        )}
      </InView>
    );
  };

  return (
    <>
      <Head>
        <title>
          Review of {review.bc?.name ? review.bc.name : review.hc?.name} -
          First-Class.com
        </title>
        <link
          rel="canonical"
          href={`${process.env.apiUrl}/hotel/review/${
            review.safename ? review.safename : router.query.id
          }`}
        />
        <meta
          name="description"
          content={replaceHTML(review.bcom?.hotel_data?.hotel_description)}
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="first-class.com" />
        <meta
          name="twitter:title"
          content={`Review of ${
            review.bc?.name ? review.bc.name : review.hc?.name
          } `}
        />
        <meta
          name="twitter:description"
          content={replaceHTML(review.bcom?.hotel_data?.hotel_description)}
        />
        <meta name="twitter:image" content={review.hc?.images[0]?.large} />
        <meta
          property="og:title"
          content={`Review of ${
            review.bc?.name ? review.bc.name : review.hc?.name
          } `}
        />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={review.hc?.images[0]?.large} />
        <meta
          property="og:description"
          content={replaceHTML(review.bcom?.hotel_data?.hotel_description)}
        />
      </Head>
      <div className={cn(styles.hotelReviewWrapper, 'bg-review-bg relative')}>
        {review.bookingAvailable && !isMobile && isSticky && (
          <div
            className={cn({
              [styles.stickySearch]: isSticky,
              hidden: !searchToggled,
            })}
            style={{ top: stickySearchTop }}
            ref={elementRef}
          >
            <Box
              sx={{
                width: '300px',
                background: 'white',
                padding: '8px',
              }}
            >
              {renderSearch(toggleSearch, 'sideSearch')}
            </Box>
          </div>
        )}
        {review.bookingAvailable && isMobile && isSticky && (
          <div
            className={cn({
              [styles.stickyMobileSearch]: isSticky,
            })}
            style={{ top: stickySearchTop }}
            ref={elementRef}
          >
            {mobileSearch && (
              <Box
                sx={{
                  width: '100%',
                  background: 'white',
                  padding: '1rem',
                  display: 'flex',
                  position: 'relative',
                }}
              >
                <IconButton
                  color="inherit"
                  onClick={() => setMobileSearch(false)}
                  aria-label="close"
                  className={styles.closeMobileSearch}
                >
                  <CloseIcon />
                </IconButton>
                <HotelSearch
                  value={searchValue}
                  viewMode={'sideSearch'}
                  hotelName={hcMetadata?.general?.HC?.name}
                  hotelId={hotelId}
                />
              </Box>
            )}
            {!mobileSearch && (
              <MobileFiltersHeader
                setSearchCollapsed={mobileSearch}
                action={() => setMobileSearch(true)}
                onPinToTop={(value) => setStickySearchTop(value)}
              />
            )}
          </div>
        )}
        <div
          className={styles.topSectionCarousel}
          style={{ height: carouselRef.current?.clientWidth / 1.777 || 200 }}
        >
          {!!review.hc?.images?.length && (
            <Carousel
              responsive={responsive}
              infinite
              customLeftArrow={<CustomLeftArrow />}
              customRightArrow={<CustomRightArrow />}
              containerClass={styles.carouselContainer}
            >
              {review.hc.images.map((img, index) => (
                <div
                  ref={carouselRef}
                  key={img?.large}
                  className={cn('relative w-full h-full', {
                    ['px-2']: !isMobile && (index === 1 || index % 3 === 1),
                  })}
                >
                  <img className={'h-full w-full'} src={img?.large} />
                </div>
              ))}
            </Carousel>
          )}
        </div>
        <div className="container sm:px-5">
          <BCBreadcrumbs headerText={review.name || review.hc?.name} />
          <div className={cn(styles.headerTop, 'font-fragment')}>
            <Tooltip
              enterTouchDelay={1}
              enterDelay={0}
              leaveTouchDelay={10000}
              title="This star rating is provided to First-Class.com by our hotel partners, and its usually determined by an official hotel
              rating organisation or another third party."
            >
              <h1 className="mb-3 lg:text-5xl sm:text-3xl font-fragment flex">
                <span
                  className={cn(
                    styles[`hotelName${hcMetadata?.general?.HC?.starRating}`]
                  )}
                >
                  {review.bc?.name ? review.bc.name : review.hc?.name}
                </span>
              </h1>
            </Tooltip>
          </div>
          <div className={cn(styles.aboutSectionWrapper)}>
            <div className={cn(styles.aboutSectionContentWrapper)}>
              <div
                className={cn(
                  styles.aboutSectionContent,
                  'flex lg:flex-col flex-wrap lg:flex-nowrap col-span-4 sm:col-span-12'
                )}
              >
                <div className={cn(styles.aboutSectionScores)}>
                  <div className="grid grid-cols-12 gap-x-5">
                    <div className="col-span-9 sm:col-span-12">
                      <div className="w-full grid grid-cols-12 gap-x-5">
                        <div
                          className={cn(
                            styles.aboutSectionContent,
                            'flex lg:flex-col flex-wrap lg:flex-nowrap col-span-4 sm:col-span-12'
                          )}
                        >
                          <div
                            className={cn(
                              styles.aboutSectionHeading,
                              'flex flex-col w-full'
                            )}
                          >
                            <div className={cn(styles.aboutSectionAddress)}>
                              <PlaceIcon />
                              <div>
                                <div>
                                  {review.bc?.address ||
                                    review.hc?.place?.fullName ||
                                    review.hc?.address}
                                </div>
                                <div>
                                  {review.bc?.zip} {review.bc?.zip && <br />}{' '}
                                  {review.bc?.country}
                                </div>
                              </div>
                            </div>
                            <div className={cn(styles.aboutSectionUserScore)}>
                              <div className={'mt-4'}>
                                {!!review.hc?.reviews?.numberOfReviews &&
                                  Object.keys(
                                    review.hc?.reviews.guestRatings
                                  ).map((key) => (
                                    <div key={key} className={'mb-2 w-full'}>
                                      <ProgressMeter
                                        label={key}
                                        progress={
                                          review.hc?.reviews.guestRatings[key] *
                                          10
                                        }
                                      />
                                    </div>
                                  ))}
                              </div>

                              {!!hcMetadata?.general?.HC?.reviews
                                ?.numberOfReviews &&
                                !!review.hc?.reviews?.guestRatings && (
                                  <div className="xs:mb-2">
                                    <CircleRating
                                      rating={Math.round(
                                        hcMetadata?.general?.HC?.reviews
                                          ?.guestRatings['OVERALL']
                                      )}
                                      text={`${hcMetadata.general.HC?.reviews.numberOfReviews} user ratings.<br/>Rating - ${hcMetadata?.general?.HC?.reviews?.guestRatings['OVERALL']}`}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>

                        <div
                          className={cn(
                            styles.aboutSectionContent,
                            'col-span-8 sm:col-span-12 sm:mt-5'
                          )}
                        >
                          <div className={cn(styles.aboutSectionText)}>
                            {review.bcom?.hotel_data?.hotel_description && (
                              <>
                                <div
                                  className={cn(
                                    styles.reviewAmenitiesHeading,
                                    'font-libre'
                                  )}
                                >
                                  About
                                </div>
                                <div className={cn('font-libre')}>
                                  {review.bcom?.hotel_data?.hotel_description}
                                </div>
                              </>
                            )}
                            <div className={'flex justify-between'}>
                              {getGeneral() && (
                                <div className={'flex flex-col w-1/2'}>
                                  <div
                                    className={cn(
                                      styles.reviewAmenitiesHeading,
                                      'font-libre mt-4'
                                    )}
                                  >
                                    General
                                  </div>
                                  <div className={cn('font-libre')}>
                                    <ul className={styles.amenityList}>
                                      {!generalExpanded &&
                                        getGeneral()
                                          .splice(0, 5)
                                          .map((amenity) => (
                                            <li
                                              className={styles.amenityListItem}
                                              key={amenity}
                                            >
                                              {amenity}
                                            </li>
                                          ))}
                                      {generalExpanded &&
                                        getGeneral().map((amenity) => (
                                          <li
                                            className={styles.amenityListItem}
                                            key={amenity}
                                          >
                                            {amenity}
                                          </li>
                                        ))}
                                    </ul>
                                    {!generalExpanded &&
                                      getGeneral().length > 5 && (
                                        <span
                                          className={styles.expandAmenity}
                                          onClick={() =>
                                            setGeneralExpanded(true)
                                          }
                                        >
                                          + Show {getGeneral().length - 5} more
                                        </span>
                                      )}
                                  </div>
                                </div>
                              )}
                              <div className={'flex flex-col w-1/2'}>
                                {getActivity() && (
                                  <div className={'flex flex-col'}>
                                    <div
                                      className={cn(
                                        styles.reviewAmenitiesHeading,
                                        'font-libre mt-4'
                                      )}
                                    >
                                      Activities
                                    </div>
                                    <div className={cn('font-libre')}>
                                      <ul className={styles.amenityList}>
                                        {!activityExpanded &&
                                          getActivity()
                                            .splice(0, 3)
                                            .map((amenity) => (
                                              <li
                                                className={
                                                  styles.amenityListItem
                                                }
                                                key={amenity}
                                              >
                                                {amenity}
                                              </li>
                                            ))}
                                        {activityExpanded &&
                                          getActivity().map((amenity) => (
                                            <li
                                              className={styles.amenityListItem}
                                              key={amenity}
                                            >
                                              {amenity}
                                            </li>
                                          ))}
                                      </ul>
                                      {!activityExpanded &&
                                        getActivity().length > 3 && (
                                          <span
                                            className={styles.expandAmenity}
                                            onClick={() =>
                                              setActivityExpanded(true)
                                            }
                                          >
                                            + Show {getActivity().length - 3}{' '}
                                            more
                                          </span>
                                        )}
                                    </div>
                                  </div>
                                )}
                                {getServices() && (
                                  <div className={'flex flex-col'}>
                                    <div
                                      className={cn(
                                        styles.reviewAmenitiesHeading,
                                        'font-libre mt-4'
                                      )}
                                    >
                                      Services
                                    </div>
                                    <div className={cn('font-libre')}>
                                      <ul className={styles.amenityList}>
                                        {!servicesExpanded &&
                                          getServices()
                                            .splice(0, 3)
                                            .map((amenity) => (
                                              <li
                                                className={
                                                  styles.amenityListItem
                                                }
                                                key={amenity}
                                              >
                                                {amenity}
                                              </li>
                                            ))}
                                        {servicesExpanded &&
                                          getServices().map((amenity) => (
                                            <li
                                              className={styles.amenityListItem}
                                              key={amenity}
                                            >
                                              {amenity}
                                            </li>
                                          ))}
                                      </ul>
                                      {!servicesExpanded &&
                                        getServices().length > 3 && (
                                          <span
                                            className={styles.expandAmenity}
                                            onClick={() =>
                                              setServicesExpanded(true)
                                            }
                                          >
                                            + Show {getServices().length - 3}{' '}
                                            more
                                          </span>
                                        )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-span-3 sm:col-span-12 flex flex-col items-center">
                      <div className={cn(styles.aboutSectionUserScoreMobile)}>
                        {!!hcMetadata?.general?.HC?.reviews?.numberOfReviews &&
                          !!review.hc?.reviews?.guestRatings && (
                            <div className="xs:mb-2">
                              <CircleRating
                                rating={Math.round(
                                  hcMetadata?.general?.HC?.reviews
                                    ?.guestRatings['OVERALL']
                                )}
                                text={`${hcMetadata.general.HC?.reviews.numberOfReviews} user ratings.<br/>Rating - ${hcMetadata?.general?.HC?.reviews?.guestRatings['OVERALL']}`}
                              />
                            </div>
                          )}
                      </div>
                      {review.bookingAvailable && !isSticky && (
                        <div
                          className={cn(styles.aboutSectionSearch)}
                          ref={elementRef}
                        >
                          {!isMobile && (
                            <Box
                              sx={{
                                background: 'white',
                                padding: '0.5rem 0.5rem',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                              }}
                            >
                              <HotelSearch
                                value={searchValue}
                                viewMode={'sideSearch'}
                                hotelName={hcMetadata?.general?.HC?.name}
                                hotelId={hotelId}
                              />
                            </Box>
                          )}
                          {!isMobile && (
                            <div className={styles.priceContainer}>
                              <div className={styles.priceContainerMoreReviews}>
                                <VintageButton
                                  href="/hotels/reviews"
                                  className={cn(
                                    styles.moreReviewsButton,
                                    'relative'
                                  )}
                                  target={'_blank'}
                                  label={'More hotel reviews'}
                                  dark={true}
                                ></VintageButton>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ThemeProvider theme={tabsTheme}>
            <Box
              sx={{
                borderBottom: 1,
                borderTop: 1,
                borderColor: '#86694B',
                marginTop: '2rem',
                scrollMarginTop: '80px',
              }}
              ref={tabsRef}
            >
              <StyledTabs value={tab} onChange={handleChange}>
                <StyledTab label="Offers" {...a11yProps(0)} />
                <StyledTab label="Map" {...a11yProps(1)} />
                <StyledTab label="Amenities" {...a11yProps(2)} />
              </StyledTabs>
            </Box>
            <SwipeableViews axis="x" index={tab} onChangeIndex={setTab}>
              <TabPanel value={tab} index={0}>
                <div className={styles.offersContainer}>
                  {loadingOffers && <div className={styles.loader} />}
                  {!loadingOffers && !offersError && (
                    <HotelOffers
                      analyticsData={analyticsData}
                      offers={hcOffers?.general?.HC?.results}
                      suppliers={hcOffers?.general?.HC?.providers}
                      nights={nights}
                    />
                  )}
                  {!loadingOffers && offersError && (
                    <div className={'font-fragment text-center mt-4 text-2xl'}>
                      Something went wrong during fetching offers, please try
                      again or later.
                    </div>
                  )}
                </div>
              </TabPanel>
              <TabPanel value={tab} index={1}>
                {hcMetadata?.general && (
                  <Box
                    sx={{
                      width: '100%',
                      padding: ['1rem 0', '1rem 0', '1rem 0'],
                      height: 'calc(600px + 2rem)',
                    }}
                  >
                    {hcMetadata && (
                      <Map
                        center={{
                          lat: review.hc?.latitude,
                          lng: review.hc?.longitude,
                        }}
                        containerStyle={{ width: '100%', height: '600px' }}
                        metaInfo={{ name: review.hc?.name }}
                      />
                    )}
                  </Box>
                )}
              </TabPanel>
              <TabPanel value={tab} index={2}>
                {hcMetadata?.general && (
                  <div className={cn(styles.amenitiesContainer, 'font-libre')}>
                    {hcMetadata.general.HC?.featureSummary.map((amenity) => {
                      return (
                        <div key={amenity.name} className={styles.amenity}>
                          <div className={styles.amenityName}>
                            {amenity.name}
                          </div>
                          <div className={amenity.content}>
                            {amenity.description.split(',').map((val) => {
                              return (
                                <div
                                  key={val}
                                  className={styles.amenityContentItem}
                                >
                                  {val}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabPanel>
            </SwipeableViews>
            <InView triggerOnce>
              {({ inView, ref }) => (
                <div ref={ref} className="w-full mx-auto mb-8">
                  {inView && review.bookingAvailable && (
                    <div className="container flex flex-col mt-8">
                      <div className="sm:hidden mb-4">
                        <Box
                          sx={{
                            width: '100%',
                            background: 'white',
                            padding: ['1rem 0', '1rem 2rem', '1rem 2rem'],
                            marginLeft: 'auto',
                            marginRight: 'auto',
                          }}
                        >
                          <span
                            className={cn(
                              styles.searchHeading,
                              'font-fragment'
                            )}
                          >
                            Find best hotel offers
                          </span>

                          <HotelSearch
                            viewMode={'mainSearch'}
                            hotelName={hcMetadata?.general?.HC?.name}
                            value={searchValue}
                          />
                        </Box>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </InView>
            <InView triggerOnce>
              {({ inView, ref }) => (
                <div ref={ref} className="w-full mx-auto mb-8">
                  {inView && (
                    <div>
                      <SocialMediaLinks />
                    </div>
                  )}
                </div>
              )}
            </InView>

            <div className="container flex justify-center items-center mt-20 mx-auto  w-48">
              <VintageButton
                href="/hotels/reviews"
                className={cn(styles.moreReviewsButton, 'relative')}
                target={'_blank'}
                label={'More hotel reviews'}
                dark={true}
              ></VintageButton>
            </div>
          </ThemeProvider>
        </div>

        {userStore && !userStore.userid && (
          <InView triggerOnce>
            {({ inView, ref }) => (
              <div ref={ref} className="w-full mx-auto my-20 sm:mb-10">
                {inView && (
                  <div>
                    <CallToSignUp
                      country={userStore?.country}
                      authCompleted={authCompleted}
                      sourcePage={'HCHotelReview'}
                    />
                  </div>
                )}
              </div>
            )}
          </InView>
        )}

        <InView triggerOnce>
          {({ inView, ref }) => (
            <div ref={ref} className="w-full mx-auto my-8 px-4 lg:px-0">
              {inView && (
                <div className="container flex justify-center items-center">
                  <RecommendationsList items={recommendation} />
                </div>
              )}
            </div>
          )}
        </InView>
      </div>
    </>
  );
};
